/**
 * Push Notification Service
 *
 * Handles Firebase Cloud Messaging (FCM) for web push notifications.
 * Manages token subscription, storage, and permission requests.
 */

import { getToken, onMessage, type MessagePayload } from 'firebase/messaging';
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  serverTimestamp,
  query,
  limit,
  type Timestamp,
} from 'firebase/firestore';
import { db, appId, getMessagingInstance } from '@/config/firebase';
import { env } from '@/config/env';
import { isPWA, isPushSupported, supportsIOSWebPush, isIOS } from '@/utils/pwaDetection';
import { withTimeout, isTimeoutError } from '@/services/utils/promiseTimeout';

// Track if service worker has been initialized
let serviceWorkerInitialized = false;

// Collection paths
const getPushTokensPath = (userId: string) => `artifacts/${appId}/users/${userId}/push_tokens`;
const getNotificationSettingsPath = (userId: string) =>
  `artifacts/${appId}/users/${userId}/notification_settings/preferences`;

/**
 * Request notification permission from the user
 * Returns true if permission was granted
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') {
    console.warn('Notifications not supported in this browser');
    return false;
  }

  // Check if already granted
  if (Notification.permission === 'granted') {
    return true;
  }

  // Check if already denied (can't re-request)
  if (Notification.permission === 'denied') {
    console.warn('Notification permission was previously denied');
    return false;
  }

  // Request permission
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
}

/**
 * Check if push notifications can be enabled for this user/device
 */
export function canEnablePushNotifications(): boolean {
  // Must support push
  if (!isPushSupported()) {
    return false;
  }

  // On iOS, must be running as PWA and iOS 16.4+
  if (isIOS()) {
    return isPWA() && supportsIOSWebPush();
  }

  // On other platforms, just need push support
  return true;
}

/**
 * Get the reason why push notifications can't be enabled
 */
export function getPushNotSupportedReason(): string | null {
  if (!isPushSupported()) {
    return 'Your browser does not support push notifications.';
  }

  if (isIOS()) {
    if (!isPWA()) {
      return 'Add this app to your Home Screen to enable notifications.';
    }
    if (!supportsIOSWebPush()) {
      return 'Push notifications require iOS 16.4 or later.';
    }
  }

  return null;
}

/**
 * Initialize push notifications on app startup.
 * This registers the service worker and passes it the Firebase config
 * for users who have already granted notification permission.
 * Should be called early in the app lifecycle.
 */
export async function initializePushNotifications(): Promise<void> {
  if (serviceWorkerInitialized) {
    return;
  }

  // Only initialize if push is supported and permission is granted
  if (!canEnablePushNotifications()) {
    return;
  }

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }

  try {
    const registration = await registerServiceWorkerWithConfig();
    if (registration) {
      serviceWorkerInitialized = true;
    }
  } catch (error) {
    console.error('[Push] Failed to initialize service worker:', error);
  }
}

/**
 * Refresh push notification token for a user.
 * Should be called periodically or when the user opens the app.
 * This ensures the FCM token is up-to-date.
 */
export async function refreshPushToken(userId: string): Promise<string | null> {
  if (!canEnablePushNotifications()) {
    return null;
  }

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return null;
  }

  try {
    // Ensure service worker is registered
    const swRegistration = await registerServiceWorkerWithConfig();
    if (!swRegistration) {
      return null;
    }

    // Get messaging instance
    const messaging = await getMessagingInstance();
    if (!messaging || !env.vapidKey) {
      return null;
    }

    // Get fresh FCM token
    const token = await getToken(messaging, {
      vapidKey: env.vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      // Save/update token in Firestore
      await savePushToken(userId, token);
      return token;
    }

    return null;
  } catch (error) {
    console.error('[Push] Error refreshing token:', error);
    return null;
  }
}

const SW_TIMEOUT_MS = 10000;

async function waitForServiceWorkerReady(
  registration: ServiceWorkerRegistration
): Promise<ServiceWorker | null> {
  try {
    await withTimeout(navigator.serviceWorker.ready, SW_TIMEOUT_MS, 'Service worker ready timeout');
  } catch (error) {
    if (isTimeoutError(error)) {
      console.warn('[Push] Service worker ready timed out, continuing anyway');
    } else {
      throw error;
    }
  }

  let sw = registration.active;
  if (!sw) {
    const installing = registration.installing || registration.waiting;
    if (installing) {
      try {
        await withTimeout(
          new Promise<void>((resolve) => {
            installing.addEventListener('statechange', function handler() {
              if (installing.state === 'activated') {
                installing.removeEventListener('statechange', handler);
                resolve();
              }
            });
          }),
          SW_TIMEOUT_MS,
          'Service worker activation timeout'
        );
      } catch (error) {
        if (isTimeoutError(error)) {
          console.warn('[Push] Service worker activation timed out');
        } else {
          throw error;
        }
      }
      sw = registration.active;
    }
  }
  return sw;
}

function sendConfigToWorker(sw: ServiceWorker | null, config: object): void {
  if (sw) {
    sw.postMessage({ type: 'FIREBASE_CONFIG', config });
  } else {
    console.warn('[Push] No active service worker to send config to');
  }
}

async function registerServiceWorkerWithConfig(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope',
    });

    const sw = await waitForServiceWorkerReady(registration);
    const firebaseConfig = JSON.parse(env.firebaseConfig);
    sendConfigToWorker(sw, firebaseConfig);

    return registration;
  } catch (error) {
    console.warn('[Push] Service worker registration failed (non-critical):', error);
    return null;
  }
}

/**
 * Subscribe to push notifications and save the token
 * Returns the FCM token if successful, null otherwise
 */
export async function subscribeToPush(userId: string): Promise<string | null> {
  try {
    // Check if we can enable push
    if (!canEnablePushNotifications()) {
      console.warn('Push notifications not supported:', getPushNotSupportedReason());
      return null;
    }

    // Request permission first
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      console.warn('Notification permission not granted');
      return null;
    }

    // Register the service worker and pass it the Firebase config
    // This must be done before getting the FCM token
    const swRegistration = await registerServiceWorkerWithConfig();
    if (!swRegistration) {
      console.warn('Failed to register service worker');
      return null;
    }

    // Get messaging instance
    const messaging = await getMessagingInstance();
    if (!messaging) {
      console.warn('Firebase Messaging not available');
      return null;
    }

    // Check for VAPID key
    if (!env.vapidKey) {
      console.warn('VAPID key not configured');
      return null;
    }

    // Get FCM token with the registered service worker
    const token = await getToken(messaging, {
      vapidKey: env.vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (!token) {
      console.warn('Failed to get FCM token');
      return null;
    }

    // Save token to Firestore
    await savePushToken(userId, token);

    return token;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return null;
  }
}

/**
 * Save an FCM token to Firestore
 */
export async function savePushToken(userId: string, token: string): Promise<void> {
  const tokenDocRef = doc(db, getPushTokensPath(userId), token);

  await setDoc(tokenDocRef, {
    token,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    // Device info for debugging
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
  });
}

/**
 * Remove an FCM token from Firestore
 */
export async function removePushToken(userId: string, token: string): Promise<void> {
  const tokenDocRef = doc(db, getPushTokensPath(userId), token);
  await deleteDoc(tokenDocRef);
}

/**
 * Unsubscribe from push notifications
 * Removes all tokens for this user
 */
export async function unsubscribeFromPush(userId: string): Promise<void> {
  try {
    const tokensRef = collection(db, getPushTokensPath(userId));
    const snapshot = await getDocs(query(tokensRef, limit(200)));

    // Delete all token documents
    const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    throw error;
  }
}

/**
 * Check if the user has any active push tokens registered
 */
export async function hasActivePushTokens(userId: string): Promise<boolean> {
  try {
    const tokensRef = collection(db, getPushTokensPath(userId));
    const snapshot = await getDocs(query(tokensRef, limit(200)));
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking push tokens:', error);
    return false;
  }
}

/**
 * Set up foreground message handler
 * Called when a push notification is received while the app is in the foreground
 */
export function setupForegroundMessageHandler(
  callback: (payload: MessagePayload) => void
): () => void {
  let unsubscribe: (() => void) | null = null;
  let isCancelled = false;

  getMessagingInstance()
    .then((messaging) => {
      // Don't set up listener if cleanup was already called
      if (messaging && !isCancelled) {
        unsubscribe = onMessage(messaging, callback);
      }
    })
    .catch((error) => {
      console.error('[Push] Error setting up foreground message handler:', error);
    });

  // Return cleanup function
  return () => {
    isCancelled = true;
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

/**
 * Notification settings types
 */
export interface NotificationSettings {
  dmNotifications: boolean;
  announcementNotifications: boolean;
  prayerNotifications: boolean;
  mentionNotifications: boolean;
  /** Leaders and admins: push when someone new joins (New Members). Missing means on. */
  outreachNotifications: boolean;
  /** Leaders and admins: email when someone new joins. Missing means on ('instant'). */
  outreachEmail?: 'instant' | 'off';
  /** New Members pushes and emails are paused until this time. */
  outreachPausedUntil?: Timestamp | null;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  dmNotifications: true,
  announcementNotifications: true,
  prayerNotifications: true,
  mentionNotifications: true,
  outreachNotifications: true,
};

/**
 * Get user's notification preferences
 */
export async function getNotificationSettings(userId: string): Promise<NotificationSettings> {
  try {
    const { getDoc } = await import('firebase/firestore');
    const settingsRef = doc(db, getNotificationSettingsPath(userId));
    const snapshot = await getDoc(settingsRef);

    if (snapshot.exists()) {
      return { ...DEFAULT_SETTINGS, ...snapshot.data() } as NotificationSettings;
    }

    return DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error getting notification settings:', error);
    return DEFAULT_SETTINGS;
  }
}

/**
 * Update user's notification preferences
 */
export async function updateNotificationSettings(
  userId: string,
  settings: Partial<NotificationSettings>
): Promise<void> {
  const settingsRef = doc(db, getNotificationSettingsPath(userId));

  await setDoc(
    settingsRef,
    {
      ...settings,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
